import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORIES = [
  { key: "fixed_100", label: "₹100 Books" },
  { key: "fixed_500", label: "₹500 Books" },
  { key: "variable", label: "Variable Books" },
];

export default function ConferenceAllocationDialog({ district, planRow, onClose, onDone }) {
  const [mode, setMode] = useState("auto");
  const [tab, setTab] = useState("fixed_100");
  const [counts, setCounts] = useState({ fixed_100: 0, fixed_500: 0, variable: 1 });
  const [needed, setNeeded] = useState({ fixed_100: 0, fixed_500: 0, variable: 1 });
  const [books, setBooks] = useState([]);
  const [selected, setSelected] = useState({ fixed_100: [], fixed_500: [], variable: [] });
  const [range, setRange] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!district || !planRow) return;
    const req = planRow.categories || {};
    const totals = {
      fixed_100: req.fixed_100?.required ?? planRow.members ?? 0,
      fixed_500: req.fixed_500?.required ?? planRow.members ?? 0,
      variable: req.variable?.required ?? 1,
    };
    setCounts(totals); setNeeded(totals);
    setSelected({ fixed_100: [], fixed_500: [], variable: [] });
    setRange({}); setMode("auto"); setTab("fixed_100");
    api.get("/conference/allocation/available-books")
      .then(({ data }) => setBooks(data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load available books"));
  }, [district, planRow]);

  const booksFor = (cat) => books.filter((b) => b.category_key === cat);
  const isSel = (cat, id) => selected[cat].includes(id);
  const toggle = (cat, id) => setSelected((s) => ({ ...s, [cat]: isSel(cat, id) ? s[cat].filter((x) => x !== id) : [...s[cat], id] }));
  const pickFirst = (cat) => setSelected((s) => ({ ...s, [cat]: booksFor(cat).slice(0, Math.max(parseInt(needed[cat], 10) || 0, 0)).map((b) => b.id) }));
  const applyRange = (cat) => {
    const r = range[cat] || {};
    const list = booksFor(cat);
    const first = list.find((b) => b.id === r.from);
    const last = list.find((b) => b.id === r.to);
    if (!first || !last) { toast.error("Choose the first and last book"); return; }
    const lo = Math.min(first.book_number_numeric, last.book_number_numeric);
    const hi = Math.max(first.book_number_numeric, last.book_number_numeric);
    const ids = list.filter((b) => b.book_number_numeric >= lo && b.book_number_numeric <= hi).map((b) => b.id);
    setSelected((s) => ({ ...s, [cat]: [...new Set([...s[cat], ...ids])] }));
  };
  const totalSelected = CATEGORIES.reduce((s, c) => s + selected[c.key].length, 0);

  const submitAuto = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/conference/allocation/district", {
        district,
        counts: { fixed_100: +counts.fixed_100 || 0, fixed_500: +counts.fixed_500 || 0, variable: +counts.variable || 0 },
      });
      const shortage = Object.entries(data.shortages || {}).filter(([, v]) => v > 0);
      toast.success(shortage.length ? `Allocated with shortage: ${shortage.map(([k, v]) => `${k}: ${v}`).join(", ")}` : "District books allocated");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Allocation failed"); }
    finally { setBusy(false); }
  };

  const submitManual = async () => {
    const ids = CATEGORIES.flatMap((c) => selected[c.key]);
    if (!ids.length) { toast.error("Select at least one book"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/conference/allocation/manual", { district, book_ids: ids });
      toast.success(`${data.allocated} of ${data.requested} books allocated to ${district}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Manual allocation failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!district} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="allocation-dialog">
        <DialogHeader>
          <DialogTitle>{district} — Allocate Receipt Books</DialogTitle>
          <DialogDescription>Automatic uses your counts and picks the lowest available book numbers. Manual lets you choose the exact books.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted/50 mb-4">
          {[["auto", "Automatic"], ["manual", "Manual — pick books"]].map(([m, l]) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`alloc-mode-${m}`}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${mode === m ? "bg-white shadow text-brand-blue" : "text-muted-foreground"}`}>{l}</button>
          ))}
        </div>

        {mode === "auto" ? (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {CATEGORIES.map((c) => (
                <div key={c.key}>
                  <Label>{c.label}</Label>
                  <Input type="number" min="0" value={counts[c.key]} onChange={(e) => setCounts((v) => ({ ...v, [c.key]: e.target.value }))} data-testid={`alloc-count-${c.key}`} />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-4">Books already held by {district} count towards these totals — only the shortfall is taken from available stock.</p>
            <Button onClick={submitAuto} disabled={busy} className="w-full bg-brand-blue text-white" data-testid="alloc-auto-submit">
              <Send className="w-4 h-4 mr-1.5" /> Allocate Automatically
            </Button>
          </div>
        ) : (
          <div>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-3 w-full mb-3">
                {CATEGORIES.map((c) => <TabsTrigger key={c.key} value={c.key} data-testid={`alloc-tab-${c.key}`}>{c.label}</TabsTrigger>)}
              </TabsList>
              {CATEGORIES.map((c) => (
                <TabsContent key={c.key} value={c.key}>
                  <div className="flex flex-wrap items-end gap-2 mb-3">
                    <div>
                      <Label>Books needed</Label>
                      <Input type="number" min="0" value={needed[c.key]} onChange={(e) => setNeeded((n) => ({ ...n, [c.key]: e.target.value }))} className="w-24" data-testid={`alloc-need-${c.key}`} />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => pickFirst(c.key)} data-testid={`alloc-pick-first-${c.key}`}>Pick first {parseInt(needed[c.key], 10) || 0}</Button>
                    <div>
                      <Label>First book</Label>
                      <Select value={range[c.key]?.from || ""} onValueChange={(v) => setRange((r) => ({ ...r, [c.key]: { ...(r[c.key] || {}), from: v } }))}>
                        <SelectTrigger className="w-32" data-testid={`alloc-range-from-${c.key}`}><SelectValue placeholder="From" /></SelectTrigger>
                        <SelectContent>{booksFor(c.key).map((b) => <SelectItem key={b.id} value={b.id}>{b.book_number}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Last book</Label>
                      <Select value={range[c.key]?.to || ""} onValueChange={(v) => setRange((r) => ({ ...r, [c.key]: { ...(r[c.key] || {}), to: v } }))}>
                        <SelectTrigger className="w-32" data-testid={`alloc-range-to-${c.key}`}><SelectValue placeholder="To" /></SelectTrigger>
                        <SelectContent>{booksFor(c.key).map((b) => <SelectItem key={b.id} value={b.id}>{b.book_number}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => applyRange(c.key)} data-testid={`alloc-range-apply-${c.key}`}>Select range</Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelected((s) => ({ ...s, [c.key]: [] }))} data-testid={`alloc-clear-${c.key}`}>Clear</Button>
                    <div className="ml-auto text-sm font-bold" data-testid={`alloc-selected-${c.key}`}>Selected {selected[c.key].length}{parseInt(needed[c.key], 10) ? ` / ${needed[c.key]}` : ""}</div>
                  </div>
                  <div className="max-h-56 overflow-auto rounded-md border border-border divide-y" data-testid={`alloc-book-list-${c.key}`}>
                    {booksFor(c.key).length === 0
                      ? <div className="p-4 text-sm text-muted-foreground">No available books in this category.</div>
                      : booksFor(c.key).map((b) => (
                        <label key={b.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50 cursor-pointer">
                          <Checkbox checked={isSel(c.key, b.id)} onCheckedChange={() => toggle(c.key, b.id)} data-testid={`alloc-book-${b.id}`} />
                          <span className="font-mono font-bold">{b.book_number}</span>
                          <span className="text-muted-foreground font-mono text-xs">{b.receipt_start}–{b.receipt_end}</span>
                          <span className="ml-auto text-[10px] text-muted-foreground">{b.leaves_count} leaves</span>
                        </label>
                      ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
            <Button onClick={submitManual} disabled={busy || !totalSelected} className="w-full bg-brand-blue text-white mt-4" data-testid="alloc-manual-submit">
              <Send className="w-4 h-4 mr-1.5" /> Allocate Selected ({totalSelected} books)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
